/*
 * Lição 17/20 — SOURCES TAB
 *
 * Você está lendo o TypeScript original desse site.
 * Source maps transformam o bundle minificado (/assets/index-abc.js)
 * de volta para os arquivos com nomes e formatação reais.
 *
 * codigo da cacada (cole em /stats): ORIGEM
 *
 * contato: linharesmatheus29@gmail.com
 */
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import './index.css'
import App from './App.tsx'
import Curriculo from './pages/Curriculo.tsx'
import Stats from './pages/Stats.tsx'
import Blog from './pages/Blog.tsx'
import LlmsTemplate from './pages/recursos/LlmsTemplate.tsx'
import WpAudit from './pages/recursos/WpAudit.tsx'
import MuPluginHardening from './pages/recursos/MuPluginHardening.tsx'
import SeoChecklist from './pages/recursos/SeoChecklist.tsx'
import SeoPoisoningDetect from './pages/recursos/SeoPoisoningDetect.tsx'
import BlipsGrowth from './pages/BlipsGrowth.tsx'
import RelatorioBlips from './pages/RelatorioBlips.tsx'
import Malu from './pages/Malu.tsx'
import Fronts from './pages/Fronts.tsx'
import Checkin from './pages/Checkin.tsx'
import { trackPageView } from './lib/track.ts'
import seoRoutes from './lib/seo-routes.json'
import { initConsole } from './console-easter.ts'
import GiftFloatingButton from './components/GiftFloatingButton.tsx'

const BLIPS_TOKEN = 'lab-9k2x'
const BLIPS_KEY = 'preview_mode'

function isBlipsUnlocked(): boolean {
  if (typeof window === 'undefined') return false
  if (window.localStorage.getItem(BLIPS_KEY) === BLIPS_TOKEN) return true
  const p = new URLSearchParams(window.location.search)
  if (p.get('k') === BLIPS_TOKEN) {
    window.localStorage.setItem(BLIPS_KEY, BLIPS_TOKEN)
    return true
  }
  return false
}

function Unlock() {
  const params = new URLSearchParams(window.location.search)
  const key = params.get('key')
  if (key === BLIPS_TOKEN) {
    window.localStorage.setItem(BLIPS_KEY, BLIPS_TOKEN)
  } else if (key === 'logout') {
    window.localStorage.removeItem(BLIPS_KEY)
  }
  return <Navigate to="/" replace />
}

function BlipsGate({ children }: { children: React.ReactNode }) {
  return <>{isBlipsUnlocked() ? children : <Navigate to="/" replace />}</>
}

const CHECKIN_TOKEN = 'eod-k7m2x9'
const CHECKIN_KEY = 'checkin_mode'

function isCheckinUnlocked(): boolean {
  if (typeof window === 'undefined') return false
  if (window.localStorage.getItem(CHECKIN_KEY) === CHECKIN_TOKEN) return true
  const p = new URLSearchParams(window.location.search)
  if (p.get('k') === CHECKIN_TOKEN) {
    window.localStorage.setItem(CHECKIN_KEY, CHECKIN_TOKEN)
    return true
  }
  return false
}

function CheckinGate({ children }: { children: React.ReactNode }) {
  return <>{isCheckinUnlocked() ? children : <Navigate to="/" replace />}</>
}

initConsole()

function PageViewTracker() {
  const location = useLocation()
  useEffect(() => { trackPageView() }, [location.pathname])
  return null
}

// Mantem title/description/canonical alinhados com a rota na navegacao do SPA.
// O HTML de cada rota publica ja chega certo do build (scripts/prerender.mjs);
// isto so cobre a troca de rota sem recarregar.
const ORIGIN = 'https://matheusedson.com'
const HOME_SEO = {
  title: document.title,
  description: document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '',
}

function SeoSync() {
  const { pathname } = useLocation()
  useEffect(() => {
    const route = (seoRoutes as Record<string, { title: string; description: string }>)[pathname]
    const seo = route ?? HOME_SEO
    document.title = seo.title
    document.querySelector('meta[name="description"]')?.setAttribute('content', seo.description)
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', ORIGIN + (route ? pathname : '/'))
  }, [pathname])
  return null
}

function GiftSlot() {
  const location = useLocation()
  if (location.pathname.startsWith('/recursos/')) return null
  if (location.pathname === '/curriculo') return null
  if (location.pathname === '/malu') return null
  if (location.pathname.startsWith('/relatorio')) return null
  if (location.pathname.startsWith('/blipsgrowth')) return null
  if (location.pathname.startsWith('/fronts')) return null
  if (location.pathname.startsWith('/checkin')) return null
  return <GiftFloatingButton />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <PageViewTracker />
      <SeoSync />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/curriculo" element={<Curriculo />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/recursos/llms-template" element={<LlmsTemplate />} />
        <Route path="/recursos/wp-audit" element={<WpAudit />} />
        <Route path="/recursos/mu-plugin-hardening" element={<MuPluginHardening />} />
        <Route path="/recursos/seo-checklist" element={<SeoChecklist />} />
        <Route path="/recursos/seo-poisoning-detect" element={<SeoPoisoningDetect />} />
        <Route path="/blipsgrowth" element={<BlipsGate><BlipsGrowth /></BlipsGate>} />
        <Route path="/blipsgrowth/" element={<BlipsGate><BlipsGrowth /></BlipsGate>} />
        <Route path="/relatorio" element={<BlipsGate><RelatorioBlips /></BlipsGate>} />
        <Route path="/relatorio/" element={<BlipsGate><RelatorioBlips /></BlipsGate>} />
        <Route path="/relatoriodezembro" element={<Navigate to="/relatorio" replace />} />
        <Route path="/relatoriodezembro/" element={<Navigate to="/relatorio" replace />} />
        <Route path="/malu" element={<Malu />} />
        <Route path="/fronts" element={<BlipsGate><Fronts /></BlipsGate>} />
        <Route path="/fronts/" element={<BlipsGate><Fronts /></BlipsGate>} />
        <Route path="/checkin" element={<CheckinGate><Checkin /></CheckinGate>} />
        <Route path="/checkin/" element={<CheckinGate><Checkin /></CheckinGate>} />
        <Route path="/unlock" element={<Unlock />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GiftSlot />
    </BrowserRouter>
  </StrictMode>,
)
